import { Box, Button, CircularProgress, Link, Stack, Tooltip, Typography } from '@mui/material';

import LightningBoltIcon from 'modules/common/components/Icons/LightningBoltIcon';
import InformationCircleIcon from 'modules/common/components/Icons/InformationCircleIcon';
import { gradients } from 'theme/mixins';

interface EnableAutoStatementsBannerProps {
    readonly institutionName: string;
    readonly onEnableClick: () => void;
    readonly isConnecting: boolean;
}

export default function EnableAutoStatementsBanner({
    institutionName,
    onEnableClick,
    isConnecting,
}: EnableAutoStatementsBannerProps) {
    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
            spacing={{ xs: 2, sm: 3 }}
            sx={{
                px: 3,
                py: 2,
                backgroundColor: 'purple.100',
            }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 1,
                        borderRadius: '59px',
                        background: gradients.primaryPurple,
                        flexShrink: 0,
                    }}>
                    <LightningBoltIcon sx={{ fontSize: 16, color: 'white' }} />
                </Box>
                <Stack>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant="h5">
                            Enable Auto Statements
                            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                                {' '}
                                for {institutionName}
                            </Box>
                        </Typography>
                        <Tooltip
                            enterTouchDelay={0}
                            title={
                                <>
                                    We securely connect to your accounts to automatically match your statement data to
                                    your transaction.{' '}
                                    <Link
                                        href="https://help.collective.com/hc/en-us/articles/47978316287763-Understanding-Automated-Statements-at-Collective"
                                        target="_blank"
                                        rel="noopener noreferrer">
                                        Learn more
                                    </Link>
                                </>
                            }
                            placement="top">
                            <span style={{ display: 'inline-flex' }}>
                                <InformationCircleIcon sx={{ fontSize: 16, color: 'cement.500', cursor: 'pointer' }} />
                            </span>
                        </Tooltip>
                    </Stack>
                    <Typography variant="body2">Skip the monthly uploads. Quick and secure setup</Typography>
                </Stack>
            </Stack>
            <Button
                variant="contained"
                color="secondary"
                onClick={onEnableClick}
                disabled={isConnecting}
                sx={{ flexShrink: 0 }}>
                {isConnecting ? <CircularProgress size={20} color="inherit" /> : 'Enable'}
            </Button>
        </Stack>
    );
}
